/**
 * @file pages/well/wellShared.js
 * @description Well Butler — shared, phone-first UI primitives that ENFORCE the
 * module's non-negotiable design DNA on every screen:
 *
 *   1. PLAIN ENGLISH + define every term inline  → <Term> (tap to read the plain
 *      meaning the first time a screen uses jargon like "TDH").
 *   2. THE LIABILITY PRESENTATION, everywhere a result appears → <ResultHeader>
 *      shows the ANSWER, a CONFIDENCE badge (High/Medium/Low + why), and the
 *      blunt verify line: "You're the pro. Check this before you trust it."
 *   3. NUMBER things the tech answers → <NumberedField> gives each input a stable
 *      number so a half-distracted field tech can map replies cleanly.
 *
 * These wrap the design-system (Card/Button/Badge) so Well Butler looks like the
 * rest of the suite but reads like a field guide, not a spec sheet.
 *
 * @see well-butler/plan/06-AUTODATA-ORDERING-FRONTEND.md §C.1
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Badge } from '../../design-system';

/** The single line that must appear under EVERY result (James's words). */
export const VERIFY_LINE = "You're the pro. Check this before you trust it.";

/* ------------------------------------------------------------------ */
/*  Term — inline plain-English definer for any piece of jargon.       */
/*  Renders the term as a dotted-underline button; tap reveals the     */
/*  plain meaning right under it. No naked jargon, ever.                */
/* ------------------------------------------------------------------ */
export function Term({ word, children }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ display: 'inline' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: 'var(--color-primary)', font: 'inherit',
          borderBottom: '2px dotted var(--color-primary)',
        }}
        aria-expanded={open}
      >
        {word}
      </button>
      {open && (
        <span
          style={{
            display: 'block', marginTop: 'var(--space-1)', padding: 'var(--space-2) var(--space-3)',
            background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)', lineHeight: 1.4,
          }}
        >
          {children}
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Map a server confidence block (or a 0..1 / 0..100 number) to a     */
/*  High / Medium / Low band + color.                                  */
/* ------------------------------------------------------------------ */
export function bandFromConfidence(confidence) {
  let band = null;
  let score = null;
  if (confidence && typeof confidence === 'object') {
    band = confidence.band || null;
    score = typeof confidence.score === 'number' ? confidence.score : null;
  } else if (typeof confidence === 'number') {
    score = confidence;
  }
  // normalize a 0..95 or 0..100 score to 0..1
  if (score != null && score > 1) score = score / 100;
  if (!band && score != null) {
    band = score >= 0.8 ? 'high' : score >= 0.5 ? 'medium' : 'low';
  }
  const label = band === 'high' ? 'High confidence'
    : band === 'medium' ? 'Medium confidence'
    : band === 'low' ? 'Low confidence'
    : 'Confidence unknown';
  const tone = band === 'high' ? 'green' : band === 'medium' ? 'yellow' : band === 'low' ? 'red' : 'gray';
  const pct = score != null ? `${Math.round(score * 100)}%` : null;
  return { band, label, tone, pct };
}

/* ------------------------------------------------------------------ */
/*  ResultHeader — the liability block. ANSWER + CONFIDENCE + VERIFY.   */
/*  Pass the engine's `confidence` object (it carries band + reasons). */
/* ------------------------------------------------------------------ */
export function ResultHeader({ answer, sub, confidence }) {
  const { tone, label, pct } = bandFromConfidence(confidence);
  const reasons = (confidence && Array.isArray(confidence.reasons))
    ? confidence.reasons.filter(Boolean) : [];
  return (
    <Card variant="elevated" padding="md" style={{ marginBottom: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-1)' }}>
            The answer
          </div>
          <div style={{ fontSize: 'var(--text-heading-md, 1.4rem)', fontWeight: 'var(--weight-bold)', color: 'var(--text-primary)', lineHeight: 1.2 }}>
            {answer}
          </div>
          {sub && (
            <div style={{ marginTop: 'var(--space-1)', color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)' }}>
              {sub}
            </div>
          )}
        </div>
        <Badge color={tone} variant="subtle">{label}{pct ? ` · ${pct}` : ''}</Badge>
      </div>

      {reasons.length > 0 && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-tertiary)', marginBottom: 'var(--space-1)' }}>
            Why this confidence
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.1rem', color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', lineHeight: 1.5 }}>
            {reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      <div
        style={{
          marginTop: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)',
          background: 'var(--color-warning-subtle, var(--surface-sunken))',
          border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
          fontWeight: 'var(--weight-semibold)', color: 'var(--text-primary)', fontSize: 'var(--text-body-sm)',
        }}
        role="note"
      >
        {VERIFY_LINE}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Disclaimer — render the engine's own disclaimer text verbatim.     */
/* ------------------------------------------------------------------ */
export function Disclaimer({ text }) {
  if (!text) return null;
  return (
    <p style={{ marginTop: 'var(--space-4)', color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)', lineHeight: 1.5 }}>
      {text}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/*  Assumptions — show the inputs/assumptions the engine used.         */
/* ------------------------------------------------------------------ */
export function Assumptions({ assumptions }) {
  if (!assumptions) return null;
  const entries = Array.isArray(assumptions)
    ? assumptions.map((a) => [a.key || a.name || '', a])
    : Object.entries(assumptions);
  if (!entries.length) return null;
  return (
    <Card variant="outline" padding="md" style={{ marginTop: 'var(--space-3)' }}>
      <div style={{ fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-2)' }}>
        What this used (assumptions)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-1_5)' }}>
        {entries.map(([k, v], i) => {
          const value = v && typeof v === 'object' ? (v.value != null ? v.value : JSON.stringify(v)) : v;
          const src = v && typeof v === 'object' ? v.source : null;
          return (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', fontSize: 'var(--text-body-sm)' }}>
              <span style={{ color: 'var(--text-secondary)' }}>{k}</span>
              <span style={{ color: 'var(--text-primary)', textAlign: 'right' }}>
                {String(value)}{src ? <em style={{ color: 'var(--text-tertiary)', fontStyle: 'normal' }}> · {src}</em> : null}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  WellPage — phone-first page shell (constrained width, back link).  */
/* ------------------------------------------------------------------ */
export function WellPage({ title, intro, children, back = '/well' }) {
  return (
    <div style={{ maxWidth: 560, width: '100%', margin: '0 auto', padding: 'var(--space-4) var(--space-3) var(--space-10)' }}>
      <div style={{ marginBottom: 'var(--space-3)' }}>
        <Link to={back} style={{ color: 'var(--color-primary)', fontSize: 'var(--text-body-sm)', textDecoration: 'none' }}>
          ← Back
        </Link>
      </div>
      {title && (
        <h1 style={{ fontSize: 'var(--text-heading-lg, 1.6rem)', fontWeight: 'var(--weight-bold)', margin: '0 0 var(--space-2)' }}>
          {title}
        </h1>
      )}
      {intro && (
        <p style={{ color: 'var(--text-secondary)', margin: '0 0 var(--space-5)', lineHeight: 1.5 }}>
          {intro}
        </p>
      )}
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  NumberedField — a labeled input row with a stable number so the    */
/*  tech can map answers cleanly (feedback-number-everything).          */
/* ------------------------------------------------------------------ */
export function NumberedField({ n, label, hint, children }) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontWeight: 'var(--weight-semibold)', marginBottom: 'var(--space-1_5)' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: '50%', background: 'var(--color-primary)',
          color: '#fff', fontSize: '0.75rem', marginRight: 'var(--space-2)',
        }}>{n}</span>
        {label}
      </label>
      {hint && (
        <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-tertiary)', margin: '0 0 var(--space-2) 30px', lineHeight: 1.4 }}>
          {hint}
        </div>
      )}
      <div style={{ marginLeft: 30 }}>{children}</div>
    </div>
  );
}

/** ErrorNote — friendly inline error for a failed engine call. */
export function ErrorNote({ error }) {
  if (!error) return null;
  const msg = typeof error === 'string' ? error : (error.message || error.code || 'Something went wrong.');
  return (
    <Card variant="outline" padding="md" style={{ borderColor: 'var(--border-error)', marginBottom: 'var(--space-4)' }}>
      <div style={{ color: 'var(--text-error)', fontWeight: 'var(--weight-semibold)' }}>Couldn’t finish that</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-sm)', marginTop: 'var(--space-1)' }}>{msg}</div>
    </Card>
  );
}
